import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UtensilsCrossed, ChevronRight, Info } from 'lucide-react';

export const IF_QUESTION = 'Would you like to follow an intermittent fasting schedule to help manage calorie intake and support fat loss?';
export const IF_HELP = 'Intermittent fasting is optional — you can reach your goal without it.';

export const scheduleFor = (meals, fasting) =>
  String(meals) === '4' ? ['Breakfast', 'Lunch', 'Snack', 'Dinner'] : fasting ? ['Lunch', 'Snack', 'Dinner'] : ['Breakfast', 'Lunch', 'Dinner'];

// Poll until the background meal-plan rebuild finishes.
const waitForRebuild = async () => {
  const started = Date.now();
  while (Date.now() - started < 150000) {
    await new Promise(r => setTimeout(r, 3000));
    try { const r = await api.get('/nutrition/meal-preferences'); if (!r.data.regenerating) return r.data; } catch { /* keep polling */ }
  }
  return null;
};

export const saveMealPrefs = async (body) => {
  const r = await api.put('/nutrition/meal-preferences', body);
  if (!r.data.regenerating) return r.data;
  toast.info('Rebuilding your meal plan with your new meal schedule…');
  const done = await waitForRebuild();
  if (done?.recommendation) toast.warning('Your plan needs your decision on meal frequency.');
  else if (done) toast.success('Your meal plan has been updated.');
  return done || r.data;
};

function Choice({ options, value, onSelect, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map(([v, label]) => (
        <button key={String(v)} type="button" onClick={() => onSelect(v)} data-testid={`${testid}-${String(v)}`}
          className={`px-3 py-2.5 rounded-xl text-sm border text-left transition-colors ${value === v ? 'border-[#00C8FF] bg-[#00C8FF]/10' : 'border-[var(--c-hair)] text-muted-foreground'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function MealPrefsEditor({ initial, onDone }) {
  const [meals, setMeals] = useState(initial?.meals_per_day || 3);
  const [fasting, setFasting] = useState(initial?.intermittent_fasting ?? null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (meals === 3 && fasting === null) return toast.error('Please answer the intermittent fasting question.');
    setSaving(true);
    try { onDone?.(await saveMealPrefs({ meals_per_day: meals, intermittent_fasting: meals === 3 ? fasting : null })); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="meal-prefs-editor">
      <div><div className="text-sm font-semibold mb-2">Meals per day</div>
        <Choice options={[[3, '3 meals'], [4, '4 meals']]} value={meals} onSelect={setMeals} testid="meal-prefs-meals" /></div>
      {meals === 3 && (
        <div><div className="text-sm font-semibold mb-1">{IF_QUESTION}</div>
          <p className="text-xs text-muted-foreground mb-2">{IF_HELP}</p>
          <Choice options={[[true, 'Yes'], [false, 'No']]} value={fasting} onSelect={setFasting} testid="meal-prefs-fasting" /></div>
      )}
      <div className="text-xs text-muted-foreground" data-testid="meal-prefs-schedule">
        Your day: {meals === 3 && fasting === null ? '—' : scheduleFor(meals, fasting).join(' · ')}
      </div>
      <Button onClick={save} disabled={saving} data-testid="meal-prefs-save" className="w-full bg-[#00C8FF] text-[#081726] font-semibold">
        {saving ? 'Saving & rebuilding…' : 'Save'}
      </Button>
    </div>
  );
}

export function MealPrefsRow() {
  const [prefs, setPrefs] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get('/nutrition/meal-preferences').then(r => setPrefs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="row-meal-prefs" className="w-full px-4 py-3.5 flex items-center gap-3 border-b border-[var(--c-hair)] last:border-0 hover:bg-[var(--c-film)] transition-colors text-left">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#65A30D]/10"><UtensilsCrossed className="w-4 h-4 text-[#65A30D]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Meal schedule</div>
          <div className="text-[11px] text-muted-foreground truncate" data-testid="row-meal-prefs-sub">{prefs ? prefs.schedule.join(' · ') : '…'}</div>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-regen-card border-[var(--c-hair)]">
          <DialogHeader><DialogTitle>Meal schedule</DialogTitle></DialogHeader>
          {prefs && <MealPrefsEditor initial={prefs} onDone={(p) => { setPrefs(p); setOpen(false); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function MealRecommendation({ onChanged }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/nutrition/meal-preferences').then(r => setPrefs(r.data)).catch(() => {}); }, []);
  const rec = prefs?.recommendation;
  if (!rec) return null;
  const decide = async (body) => {
    setBusy(true);
    try { setPrefs(await saveMealPrefs(body)); await onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[#00C8FF]/40 bg-[#00C8FF]/10 p-4 space-y-3" data-testid="meal-recommendation">
      <div className="flex items-start gap-2.5">
        <Info className="w-4 h-4 text-[#00C8FF] mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-sm">We recommend 4 meals per day</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed" data-testid="meal-recommendation-reason">{rec.reason}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button disabled={busy} onClick={() => decide({ meals_per_day: 4 })} data-testid="meal-recommendation-accept" className="bg-[#00C8FF] text-[#081726] font-semibold">
          {busy ? 'Building your plan…' : 'Switch to 4 meals'}
        </Button>
        <Button disabled={busy} variant="outline" onClick={() => decide({ meals_per_day: 3, intermittent_fasting: !!prefs.intermittent_fasting, keep_three_meals: true })}
          data-testid="meal-recommendation-keep" className="border-[var(--c-hair)]">
          Keep 3 meals
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">If you keep 3 meals, some days may fall short of your target and your coach will review them.</p>
    </div>
  );
}

export const MEAL_STRUCTURES = [
  { value: '3-if', meals_per_day: 3, eating_style: 'Intermittent Fasting', label: '3 — Lunch, Snack, Dinner (fasting)' },
  { value: '3', meals_per_day: 3, eating_style: 'Standard', label: '3 — Breakfast, Lunch, Dinner' },
  { value: '4', meals_per_day: 4, eating_style: 'Standard', label: '4 — Breakfast, Lunch, Snack, Dinner' },
];
export const structureFields = (v) => { const s = MEAL_STRUCTURES.find(x => x.value === v) || MEAL_STRUCTURES[0]; return { meals_per_day: s.meals_per_day, eating_style: s.eating_style }; };

export function AdminMealStructure({ value, onChange, fromClient, testid }) {
  if (fromClient) return <div className="text-xs text-muted-foreground pt-2" data-testid={testid}>From the client's saved meal schedule</div>;
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      className="w-full h-10 rounded-md bg-transparent border border-[var(--c-hair)] px-2 text-xs">
      {MEAL_STRUCTURES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
}
